import axios from 'axios';

// ============================================
// API CONFIGURATION
// ============================================

/**
 * Get the base URL for API calls based on environment
 * Priority: Environment variable > Production URL
 */
const getBaseURL = () => {
  // For Create React App, use process.env
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Default to production URL
  return 'https://worganaturals.com/api';
};

// ============================================
// AXIOS INSTANCE CREATION
// ============================================

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// ============================================
// REQUEST INTERCEPTOR
// ============================================

/**
 * Add authentication token to requests if available
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================

/**
 * Handle response errors globally
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      // Optionally redirect to login
      if (window.location.pathname.includes('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    
    // Log error for debugging
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.error || error.message,
    });
    
    return Promise.reject(error);
  }
);

// ============================================
// BLOG API ENDPOINTS
// ============================================

export const blogAPI = {
  /**
   * Get all blog posts
   */
  getAll: () => apiClient.get('/blog'),
  
  /**
   * Get blog post by ID
   */
  getById: (id) => apiClient.get(`/blog/${id}`),
  
  /**
   * Get blog post by slug
   */
  getBySlug: (slug) => apiClient.get(`/blog/slug/${slug}`),
  
  /**
   * Create new blog post (Admin only)
   */
  create: (data) => apiClient.post('/blog', data),
  
  /**
   * Update blog post (Admin only)
   */
  update: (id, data) => apiClient.put(`/blog/${id}`, data),
  
  /**
   * Delete blog post (Admin only)
   */
  delete: (id) => apiClient.delete(`/blog/${id}`),
  
  /**
   * Upload blog image (Admin only)
   */
  uploadImage: (formData) => apiClient.post('/blog/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// ============================================
// NEWS API ENDPOINTS
// ============================================

export const newsAPI = {
  /**
   * Get all news articles
   */
  getAll: () => apiClient.get('/news'),
  
  /**
   * Get news article by ID
   */
  getById: (id) => apiClient.get(`/news/${id}`),
  
  /**
   * Get news article by slug
   */
  getBySlug: (slug) => apiClient.get(`/news/slug/${slug}`),
  
  /**
   * Create new news article (Admin only)
   */
  create: (data) => apiClient.post('/news', data),
  
  /**
   * Update news article (Admin only)
   */
  update: (id, data) => apiClient.put(`/news/${id}`, data),
  
  /**
   * Delete news article (Admin only)
   */
  delete: (id) => apiClient.delete(`/news/${id}`),
  
  /**
   * Upload news image (Admin only)
   */
  uploadImage: (formData) => apiClient.post('/news/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// ============================================
// ADMIN API ENDPOINTS
// ============================================

export const adminAPI = {
  /**
   * Admin login
   */
  login: (credentials) => apiClient.post('/admin/login', credentials),
  
  /**
   * Get current admin user info
   */
  getCurrentUser: () => apiClient.get('/admin/me'),
  
  /**
   * Change password
   */
  changePassword: (data) => apiClient.post('/admin/change-password', data),
  
  // ============================================
  // USER MANAGEMENT (Super Admin only)
  // ============================================
  
  /**
   * Get all admin users (Super Admin only)
   */
  getAllUsers: () => apiClient.get('/admin/users'),
  
  /**
   * Create new admin user (Super Admin only)
   */
  createUser: (data) => apiClient.post('/admin/users', data),
  
  /**
   * Update admin user (Super Admin only)
   */
  updateUser: (id, data) => apiClient.put(`/admin/users/${id}`, data),
  
  /**
   * Delete admin user (Super Admin only)
   */
  deleteUser: (id) => apiClient.delete(`/admin/users/${id}`),
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('adminToken');
};

/**
 * Get authentication token
 */
export const getAuthToken = () => {
  return localStorage.getItem('adminToken');
};

/**
 * Set authentication token
 */
export const setAuthToken = (token) => {
  localStorage.setItem('adminToken', token);
};

/**
 * Remove authentication token
 */
export const removeAuthToken = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUser');
};

/**
 * Get current user from localStorage
 */
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('adminUser');
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Check if user has specific role
 */
export const hasRole = (role) => {
  const user = getCurrentUser();
  return user?.role === role;
};

/**
 * Check if user is super admin
 */
export const isSuperAdmin = () => {
  return hasRole('super_admin');
};

/**
 * Check if user is admin or super admin
 */
export const isAdmin = () => {
  const user = getCurrentUser();
  return user?.role === 'admin' || user?.role === 'super_admin';
};

/**
 * Check if user can edit content (admin or super admin)
 */
export const canEdit = () => {
  return isAdmin();
};

/**
 * Check if user can delete content (admin or super admin)
 */
export const canDelete = () => {
  return isAdmin();
};

/**
 * Get base URL for direct access
 */
export const getAPIBaseURL = getBaseURL;

// Export axios instance as default
export default apiClient;
